import { useFirebase } from 'contexts/FirebaseContext';
import { onChildAdded, onValue, ref, remove, set, update } from 'firebase/database';
import { LiveEventSearch, LiveEventWish, LiveTrackResult } from 'models/LiveEvent';
import { useCallback } from 'react';
import { uuid } from 'utils/uuid';

/**
 * Sanitizes an object to ensure no `undefined` values are sent to Firebase Realtime Database.
 */
function sanitizeForRtdb<T>(obj: T): T {
  return JSON.parse(
    JSON.stringify(obj, (_, value) => (value === undefined ? null : value))
  );
}

export function useLiveEvent() {
  const { rtdb } = useFirebase();

  /**
   * Guest submits a search request to the DJ's live queue.
   */
  const submitSearch = useCallback(
    async (
      djUserId: string,
      eventId: string,
      requesterId: string,
      title: string,
      artist: string
    ): Promise<string> => {
      const searchId = uuid();
      const searchRef = ref(rtdb, `liveEvents/${djUserId}/${eventId}/searches/${searchId}`);

      const searchData: LiveEventSearch = {
        id: searchId,
        userId: djUserId,
        eventId,
        requesterId,
        title: title.trim(),
        artist: artist.trim(),
        createdAt: Date.now(),
        status: 'pending',
      };

      await set(searchRef, sanitizeForRtdb(searchData));
      return searchId;
    },
    [rtdb]
  );

  /**
   * Guest listens to search results written back by the DJ.
   */
  const listenToSearchResults = useCallback(
    (
      djUserId: string,
      eventId: string,
      searchId: string,
      callback: (results: LiveTrackResult[], status: 'pending' | 'completed') => void
    ) => {
      const searchRef = ref(rtdb, `liveEvents/${djUserId}/${eventId}/searches/${searchId}`);

      const unsubscribe = onValue(
        searchRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            return;
          }

          const data = snapshot.val() as LiveEventSearch;
          if (data && data.status === 'completed') {
            callback(data.results || [], 'completed');
          }
        },
        (error) => {
          console.error('Error listening to search results:', error);
          callback([], 'completed');
        }
      );

      return unsubscribe;
    },
    [rtdb]
  );

  /**
   * Guest submits a live wish selected from search results.
   * Cleans up the corresponding search request if provided.
   */
  const submitWish = useCallback(
    async (
      djUserId: string,
      eventId: string,
      requesterId: string,
      track: LiveTrackResult,
      searchIdToClean?: string
    ): Promise<string> => {
      const wishId = uuid();
      const wishRef = ref(rtdb, `liveEvents/${djUserId}/${eventId}/wishes/${wishId}`);
      const rawLocalId =
        typeof track.localId === 'number'
          ? track.localId
          : typeof track.id === 'number'
          ? track.id
          : undefined;

      const wishData: LiveEventWish = {
        id: wishId,
        userId: djUserId,
        eventId,
        requesterId,
        localId: rawLocalId,
        track,
        createdAt: Date.now(),
        date: new Date().toISOString(),
      };

      await set(wishRef, sanitizeForRtdb(wishData));

      if (searchIdToClean) {
        try {
          const searchRef = ref(rtdb, `liveEvents/${djUserId}/${eventId}/searches/${searchIdToClean}`);
          await remove(searchRef);
        } catch (err) {
          console.warn('Failed to remove search entry after wish submission:', err);
        }
      }

      return wishId;
    },
    [rtdb]
  );

  /**
   * DJ listens to incoming search requests and processes them.
   */
  const listenToSearches = useCallback(
    (
      djUserId: string,
      eventId: string,
      onSearch: (search: LiveEventSearch) => Promise<void> | void
    ) => {
      const searchesRef = ref(rtdb, `liveEvents/${djUserId}/${eventId}/searches`);

      const unsubscribe = onChildAdded(
        searchesRef,
        (snapshot) => {
          if (!snapshot.exists()) return;
          const search = snapshot.val() as LiveEventSearch;
          if (search && search.status === 'pending') {
            onSearch(search);
          }
        },
        (error) => {
          console.error('Error listening to searches:', error);
        }
      );

      return unsubscribe;
    },
    [rtdb]
  );

  /**
   * DJ responds to a search with matching local library tracks (up to 10).
   */
  const answerSearch = useCallback(
    async (
      djUserId: string,
      eventId: string,
      searchId: string,
      results: LiveTrackResult[]
    ): Promise<void> => {
      const searchRef = ref(rtdb, `liveEvents/${djUserId}/${eventId}/searches/${searchId}`);
      const limitedResults = results.slice(0, 10);

      await update(
        searchRef,
        sanitizeForRtdb({
          results: limitedResults,
          status: 'completed',
        })
      );
    },
    [rtdb]
  );

  /**
   * DJ listens to live wishes, sorted newest first.
   */
  const listenToLiveWishes = useCallback(
    (djUserId: string, eventId: string, callback: (wishes: LiveEventWish[]) => void) => {
      const wishesRef = ref(rtdb, `liveEvents/${djUserId}/${eventId}/wishes`);

      const unsubscribe = onValue(
        wishesRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            callback([]);
            return;
          }

          const wishesObj = snapshot.val() as Record<string, LiveEventWish>;
          const wishesList: LiveEventWish[] = Object.values(wishesObj || {});

          // Sort newest first
          wishesList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

          callback(wishesList);
        },
        (error) => {
          console.error('Error listening to live wishes:', error);
          callback([]);
        }
      );

      return unsubscribe;
    },
    [rtdb]
  );

  return {
    submitSearch,
    listenToSearchResults,
    submitWish,
    listenToSearches,
    answerSearch,
    listenToLiveWishes,
  };
}
